import * as actions from './actions'
import { getUserInfo as selectUserInfo, getServices } from './selectors'
import { SUBSCRIPTION_STATE, DISCONNECT_REASONS } from '../constants'

import { logManager, API_LOG_TAG } from '../../logs'
import jwtDecode from 'jwt-decode'
const log = logManager.getLogger('AUTH')

/**
 * Authentication API.
 * @method api
 * @param  {Function} $0
 * @param  {Function} $0.dispatch - The redux store's dispatch function.
 * @param  {Function} $0.getState - The redux store's getState function.
 * @return {Object} api - The authentication API object.
 */
export default function api ({ dispatch, getState }) {
  return {
    /**
     * Connect with user credentials to any backend services that the SDK instance deals with.
     *
     * @public
     * @deprecated Since version 4.19.0. Please see {@link apisetCredentials setCredentials} and {@link services.subscribe subscribe} for new method of connecting.
     * @memberof api
     * @requires userCredentialsAuth
     * @method connect
     * @param {Object} credentials The credentials object.
     * @param {string} credentials.username The username including the application's domain.
     * @param {string} credentials.password The user's password.
     * @param {string} [credentials.authname] The user's authorization name.
     * @param {Object} [options] The options object for non-credential options.
     * @param {boolean} [options.forceLogOut] Force the oldest connection to log out if too many simultaneous connections. Link only.
     * @param {string} [options.clientCorrelator] Unique ID for the client. This is used by the platform to identify an instance of the application used by the specific device.
     * @example
     * client.connect({
     *   username: 'alfred@example.com',
     *   password: '********'
     *   authname: '********'
     * }, {
     *   forceLogOut: true
     * });
     */
    /**
     * Connect with user credentials to any backend services that the SDK instance deals with.
     *
     * @public
     * @deprecated Since version 4.19.0. Please see {@link apisetCredentials setCredentials} and {@link services.subscribe subscribe} for new method of connecting.
     * @memberof api
     * @requires userCredentialsDAKAuth
     * @method connect
     * @param {Object} credentials The credentials object.
     * @param {string} credentials.domainApiKey The Api key for the application's domain.
     * @param {string} credentials.username The username without the application's domain.
     * @param {string} credentials.password The user's password.
     * @param {Object} [options] The options object for non-credential options.
     * @param {string} [options.clientCorrelator] Unique ID for the client. This is used by the platform to identify an instance of the application used by the specific device.
     * @example
     * client.connect({
     *   domainApiKey: 'DAK1111111111111111111',
     *   username: 'alfred',
     *   password: '********'
     * });
     */
    /**
     * Connect by providing an access token to any backend services that the SDK instance deals with.
     * You can optionally provide a refresh token and the SDK will automatically get new access tokens.
     *
     * @public
     * @deprecated Since version 4.19.0. Please see {@link apisetCredentials setCredentials} and {@link services.subscribe subscribe} for new method of connecting.
     * @memberof api
     * @requires accessTokenAuth
     * @method connect
     * @param {Object} credentials The credentials object.
     * @param {string} credentials.username The username without the application's domain.
     * @param {string} credentials.accessToken An access token for the user with the provided user Id.
     * @param {string} [credentials.refreshToken] A refresh token for the same user.
     * @param {number} [credentials.expires] The time in seconds until the access token will expire.
     * @param {Object} [options] The options object for non-credential options.
     * @param {string} [options.clientCorrelator] Unique ID for the client. This is used by the platform to identify an instance of the application used by the specific device.
     * @example
     * client.connect({
     *   username: 'alfred@example.com',
     *   accessToken: 'AT0V1fswAiJadokx1iJMQdG04pRf',
     *   refreshToken: 'RTG9SV3QAoJaeUSEQCZAHqrhde1yT',
     *   expires: 3600
     * });
     */
    /**
     * Connect by providing an access token to any backend services that the SDK instance deals with.
     *
     * @public
     * @deprecated Since version 4.19.0. Please see {@link apisetCredentials setCredentials} and {@link services.subscribe subscribe} for new method of connecting.
     * @memberof api
     * @requires accessTokenAuthCIM
     * @method connect
     * @param {Object} credentials The credentials object.
     * @param {string} credentials.username The username without the application's domain.
     * @param {string} credentials.accessToken An access token for the user with the provided user Id.
     * @param {number} [credentials.expires] The time in seconds until the access token will expire.
     * @param {Object} [options] The options object for non-credential options.
     * @param {string} [options.clientCorrelator] Unique ID for the client. This is used by the platform to identify an instance of the application used by the specific device.
     * @example
     * client.connect({
     *   username: 'alfred@example.com',
     *   accessToken: 'AT0V1fswAiJadokx1iJMQdG04pRf',
     *   expires: 3600
     * });
     */
    /**
     * Connect by providing a username and HMAC token. This connects to any backend services that the SDK instance deals with.
     * An HMAC token is used to verify a user via the user's authorization within an organization.
     * HMAC tokens are generated by using the HmacSHA1 algorithm and a key on a data object
     * containing an `authenticationTokenRequest` object with the following properties:
     * - subscriberId - The user's subscriber ID in the organization.
     * - organizationId - The ID of the organization the user is a part of.
     *
     * @public
     * @deprecated Since version 4.19.0. Please see {@link apisetCredentials setCredentials} and {@link services.subscribe subscribe} for new method of connecting.
     * @memberof api
     * @requires hmacTokenAuth
     * @method connect
     * @param {Object} credentials The credentials object.
     * @param {string} credentials.username The username.
     * @param {string} credentials.hmacToken An HMAC token for the user with the provided user ID.
     * @param {Object} [options] The options object for non-credential options.
     * @param {boolean} [options.forceLogOut] Force the oldest connection to log out if too many simultaneous connections.
     * @param {string} [options.clientCorrelator] Unique ID for the client. This is used by the platform to identify an instance of the application used by the specific device.
     * @example
     * const hmacToken = HmacSHA1Algorithm({
     *   authenticationTokenRequest: {
     *     subscriberId: 'alfred',
     *     organizationId: 'example.com'
     *   }
     * }, key)
     *
     * client.connect({
     *   username: 'alfred@example.com',
     *   hmacToken
     * }, {
     *   forceLogOut: true
     * });
     */
    /**
     * Connect by providing a refresh token to any backend services that the SDK instance deals with.
     *
     * @public
     * @deprecated Since version 4.19.0. Please see {@link apisetCredentials setCredentials} and {@link services.subscribe subscribe} for new method of connecting.
     * @memberof api
     * @requires accessTokenAuth
     * @method connect
     * @param {Object} credentials The credentials object.
     * @param {string} credentials.username The username without the application's domain.
     * @param {string} credentials.refreshToken A refresh token for the same user.
     * @param {number} [credentials.expires] The time in seconds until the access token will expire.
     * @param {Object} [options] The options object for non-credential options.
     * @param {string} [options.clientCorrelator] Unique ID for the client. This is used by the platform to identify an instance of the application used by the specific device.
     * @example
     * client.connect({
     *   username: 'alfred@example.com',
     *   refreshToken: 'RTG9SV3QAoJaeUSEQCZAHqrhde1yT'
     *   expires: 3600
     * });
     */
    /**
     * Connect by providing an oAuth token to any backend services that the SDK instance deals with.
     *
     * @public
     * @deprecated Since version 4.19.0. Please see {@link apisetCredentials setCredentials} and {@link services.subscribe subscribe} for new method of connecting.
     * @memberof api
     * @requires oauthToken
     * @method connect
     * @param {Object} credentials The credentials object.
     * @param {string} credentials.username The username without the application's domain.
     * @param {string} credentials.oauthToken An OAuth token provided by an outside service.
     * @param {Object} [options] The options object for non-credential options.
     * @param {string} [options.clientCorrelator] Unique ID for the client. This is used by the platform to identify an instance of the application used by the specific device.
     * @example
     * client.connect({
     *   username: 'alfred@example.com',
     *   oauthToken: 'RTG9SV3QAoJaeUSEQCZAHqrhde1yT'
     * });
     */
    /**
     * Connect by providing a bearer access token to any backend services that the SDK instance deals with.
     *
     * @public
     * @deprecated Since version 4.19.0. Please see {@link apisetCredentials setCredentials} and {@link services.subscribe subscribe} for new method of connecting.
     * @memberof api
     * @requires bearerAccessTokenAuth
     * @method connect
     * @param {Object} credentials The credentials object.
     * @param {string} credentials.username The username without the application's domain.
     * @param {string} credentials.bearerAccessToken A bearerAccessToken retrieved using the authentication APIs of the platform.
     * @param {Object} [options] The options object for non-credential options.
     * @param {string} [options.clientCorrelator] Unique ID for the client. This is used by the platform to identify an instance of the application used by the specific device.
     * @example
     * client.connect({
     *   username: 'alfred@example.com',
     *   bearerAccessToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...'
     * });
     */
    connect (credentials, options) {
      // We won't expose oauthToken because it essentially acts as a password being used in conjunction with username
      // ..and passwords should NOT be logged.
      log.debug(API_LOG_TAG + 'connect: ', credentials.username)
      dispatch(actions.connect(credentials, options))
    },

    /**
     * Disconnects from the backend. This will close the websocket and you will stop receiving events.
     *
     * @public
     * @deprecated Since version 4.19.0. Please see {@link services.unsubscribe unsubscribe} for new method of disconnecting.
     * @requires userCredentialsAuth
     * @requires userCredentialsDAKAuth
     * @requires accessTokenAuth
     * @requires accessTokenAuthCIM
     * @memberof api
     * @method disconnect
     */
    disconnect () {
      log.debug(API_LOG_TAG + 'disconnect')
      dispatch(actions.disconnect())
    },

    /**
     * If you're authenticating with tokens that expire and have not provided a refresh token to the `connect` function, you can update your access token with `updateToken` before it expires to stay connected.
     *
     * @public
     * @memberof api
     * @requires accessTokenAuth
     * @requires accessTokenAuthCIM
     * @method updateToken
     * @param {Object} credentials The credentials object.
     * @param {string} credentials.username The username without the application's domain.
     * @param {string} credentials.accessToken An access token for the user with the provided user Id.
     */
    /**
     * If you're authenticating with tokens that expire and have not provided a refresh token to the `connect` function, you can update your access token with `updateToken` before it expires to stay connected.
     *
     * @public
     * @memberof api
     * @requires oauthToken
     * @method updateToken
     * @param {Object} credentials The credentials object.
     * @param {string} credentials.username The username without the application's domain.
     * @param {string} credentials.oauthToken An OAuth token provided by an outside service.
     * @example
     * client.updateToken({
     *   username: 'alfred@example.com',
     *   oauthToken: 'RTG9SV3QAoJaeUSEQCZAHqrhde1yT'
     * });
     */
    /**
     * If you're authenticating with tokens that expire, you can update your access token with `updateToken` before it expires to stay connected.
     *
     * @public
     * @memberof api
     * @requires bearerAccessTokenAuth
     * @method updateToken
     * @param {Object} credentials The credentials object.
     * @param {string} credentials.username The username without the application's domain.
     * @param {string} credentials.bearerAccessToken A bearerAccessToken provided by an outside service.
     * @example
     * client.updateToken({
     *   username: 'alfred@example.com',
     *   bearerAccessToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...'
     * });
     */
    updateToken (credentials) {
      // We won't expose oauthToken because it essentially acts as a password being used in conjunction with username
      // ..and passwords should NOT be logged.
      log.debug(API_LOG_TAG + 'updateToken: ', credentials.username)
      dispatch(actions.refreshTokens(credentials))
    },

    /**
     * Updates the current connection.
     *
     * @public
     * @memberof api
     * @requires updateConnection
     * @method updateConnection
     * @param {Object} connection
     * @param {Array} connection.services Services to subscribe to for notifications.
     * @example
     * client.updateConnection({
     *   services: ['IM', 'Presence', 'call']
     * })
     */
    updateConnection (connection) {
      log.debug(API_LOG_TAG + 'updateConnection: ', connection)
      dispatch(actions.updateSubscription(connection))
    },

    /**
     * Retrieves information about the current user.
     *
     * @public
     * @memberof api
     * @method getUserInfo
     * @returns {Object} user The user data.
     * @returns {string} user.username The username of the current user. Note that this username can take different encoded forms.
     *                                 It's not meant to be displayed to a user.
     * @returns {string} user.token The current access token.
     */
    getUserInfo () {
      log.debug(API_LOG_TAG + 'getUserInfo')
      return selectUserInfo(getState())
    },

    /**
     * Get the connection state.
     *
     * @public
     * @memberof api
     * @requires connect
     * @method getConnection
     * @returns {Object} connection The connection state.
     * @returns {boolean} connection.isConnected Whether the authenticated user is currently connected.
     * @returns {boolean} connection.isPending Whether the authenticated user's connection is currently pending.
     * @returns {Object} connection.error The error object if an error occurred.
     * @returns {string} connection.error.message The error message.
     * @returns {string} connection.error.stack The stack trace of the error.
     */
    getConnection () {
      log.debug(API_LOG_TAG + 'getConnection')
      const { isConnected, isPending, error } = getState().authentication
      return {
        isConnected,
        isPending,
        error,
        subscription: getServices(getState())
      }
    },

    /**
     * Retrieves the services that the user is subscribed for.
     *
     * @public
     * @memberof api
     * @requires services
     * @method getServices
     * @return {Array} A list of subscribed-to services.
     */
    getServices () {
      log.debug(API_LOG_TAG + 'getServices')
      return getServices(getState())
    },

    /**
     * Possible subscription states of the user.
     *
     * @public
     * @memberof api
     * @requires connect
     * @property {string} FULL All requested feature subscriptions exist.
     * @property {string} PARTIAL Some feature subscriptions exist.
     * @property {string} NONE No feature subscriptions exist.
     */
    subscriptionStates: SUBSCRIPTION_STATE,

    /**
     * Possible reasons for disconnecting.
     *
     * @public
     * @memberof api
     * @requires connect
     * @property {string} GONE Connection was terminated by the server
     * @property {string} LOST_CONNECTION Internet connection was lost
     * @property {string} WS_OVERRIDDEN WebSocket was overridden by the server
     */
    disconnectReasons: DISCONNECT_REASONS,

    /**
     * Sets the authentication tokens necessary to make requests to the platform. The access token
     * provided establishes what can be accessed by the SDK. The identity token represents who is authenticated.
     *
     * @public
     * @memberof api
     * @requires cpaasAuth
     * @method setTokens
     * @param {Object} params Dictionary object of parameters.
     * @param {string} params.accessToken An access token retrieved using the authentication APIs of the platform.
     * @param {string} params.idToken An identity token retrieved using the authentication APIs of the platform.
     * @example
     * client.setTokens({
     *   accessToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
     *   idToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...'
     * })
     */
    setTokens ({ accessToken, idToken }) {
      // We won't log both tokens, just the id one, so that we can still be able to debug.
      log.debug(API_LOG_TAG + 'setTokens: ', idToken)

      // validate token
      try {
        const jwt = jwtDecode(idToken)
        dispatch(actions.setTokens({ accessToken, idToken: jwt }))
      } catch (error) {
        log.error(`${idToken} is not a valid JWT`)
      }
    },

    /**
     * Sets the user credentials necessary to make requests to the platform.
     *
     * @public
     * @memberof api
     * @requires userCredentialsAuth
     * @method setCredentials
     * @param {Object} credentials The credentials object.
     * @param {string} credentials.username The username including the application's domain.
     * @param {string} credentials.password The user's password.
     * @param {string} [credentials.authname] The user's authorization name.
     * @example
     * client.setCredentials({
     *   username: 'alfred@example.com',
     *   password: '********'
     *   authname: '********'
     * });
     */
    /**
     * Sets the username and HMAC token necessary to make requests to the platform.
     * An HMAC token is used to verify a user via the user's authorization within an of organization.
     * HMAC tokens are generated by using the HmacSHA1 algorithm and a key on a data object
     * containing an `authenticationTokenRequest` object with the following properties:
     * - subscriberId - The user's subscriber ID in the organization.
     * - organizationId - The ID of the organization the user is a part of.
     *
     * @public
     * @memberof api
     * @requires hmacTokenAuth
     * @method setCredentials
     * @param {Object} credentials The credentials object.
     * @param {string} credentials.username The username.
     * @param {string} credentials.hmacToken An HMAC token for the user with the provided user ID.
     * @example
     * const hmacToken = HmacSHA1Algorithm({
     *   authenticationTokenRequest: {
     *     subscriberId: 'alfred',
     *     organizationId: 'example.com'
     *   }
     * }, key)
     *
     * client.setCredentials({
     *   username: 'alfred@example.com',
     *   hmacToken
     * });
     */
    /**
     * Provides a bearerAccessToken to any backend services that the SDK instance deals with.
     * The bearerAccessToken provided establishes what can be accessed by the SDK.
     *
     * @public
     * @memberof api
     * @requires bearerAccessTokenAuth
     * @method setCredentials
     * @param {Object} credentials The credentials object.
     * @param {string} credentials.username The username without the application's domain.
     * @param {string} credentials.bearerAccessToken A bearerAccessToken retrieved using the authentication APIs of the platform.
     * @example
     * client.setCredentials({
     *   username: 'alfred@example.com',
     *   bearerAccessToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...'
     * });
     */
    /**
     * Sets the authentication tokens necessary to make requests to the platform. The bearerAccessToken
     * provided establishes what can be accessed by the SDK. The identity token represents who is authenticated.
     *
     * @public
     * @memberof api
     * @requires cpaasAuth
     * @method setCredentials
     * @param {Object} credentials The credentials object.
     * @param {string} credentials.bearerAccessToken A bearerAccessToken retrieved using the authentication APIs of the platform.
     *                                               The bearerAccessToken is same as the access token for the cpaas platform.
     * @param {string} credentials.idToken An identity token retrieved using the authentication APIs of the platform.
     * @example
     * client.setCredentials({
     *   bearerAccessToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
     *   idToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...'
     * });
     */
    /**
     * Provides an access token to any backend services that the SDK instance deals with.
     * The access token provided establishes what can be accessed by the SDK.
     *
     * @public
     * @memberof api
     * @requires accessTokenAuthCIM
     * @method setCredentials
     * @param {Object} credentials The credentials object.
     * @param {string} credentials.username The username without the application's domain.
     * @param {string} credentials.accessToken An accessToken retrieved using the authentication APIs of the platform. Used for CIM authentication.
     * @example
     * client.setCredentials({
     *   username: 'alfred@example.com',
     *   accessToken: 'ATgtBl8QAoJaeeJU!zhARKBYaN2BUxFQsc8F...'
     * });
     */
    setCredentials ({ username, password, authname, hmacToken, bearerAccessToken, idToken, accessToken }) {
      log.debug(API_LOG_TAG + 'setCredentials: ', username)
      // TODO: Refactor into multiple actions for each credential mode
      // TODO: Validation of two authentication possibility scenario
      dispatch(
        actions.setCredentials({
          username,
          password,
          authname,
          hmacToken,
          bearerAccessToken,
          idToken,
          accessToken
        })
      )
    }
  }
}
